/**
 * Cutscene Editor Types
 * Defines the data model for cutscene events and configuration
 */

import { Direction } from "types/battle-engine/battle";

// ============================================================================
// Position & Camera
// ============================================================================

export interface Position {
  x: number;
  y: number;
}

export interface CameraConfig {
  /** Whether camera is bounded to map edges */
  bounded: boolean;
  /** Initial zoom level (1 = 100%) */
  initialZoom: number;
  /** Initial camera position */
  initialPosition: Position;
}

// ============================================================================
// Easing Functions
// ============================================================================

export type EasingType =
  | "linear"
  | "easeIn"
  | "easeOut"
  | "easeInOut"
  | "easeInQuad"
  | "easeOutQuad"
  | "easeInOutQuad"
  | "easeInCubic"
  | "easeOutCubic"
  | "easeInOutCubic";

// ============================================================================
// Base Event
// ============================================================================

export interface BaseEvent {
  /** Unique identifier for this event */
  id: string;
  /** Event type discriminator */
  type: string;
  /** If true, engine awaits completion before starting next event */
  blocking: boolean;
}

// ============================================================================
// Pokemon Events
// ============================================================================

export interface SpawnPokemonEvent extends BaseEvent {
  type: "spawn_pokemon";
  /** Unique actor ID to reference this Pokemon in later events */
  actorId: string;
  /** Base Pokemon name (e.g., "pikachu", "charizard") */
  pokemonName: string;
  /** Spawn position */
  position: Position;
  /** Initial facing direction */
  direction: Direction;
  /** Initial animation (e.g., "Idle", "Walk", "Attack") */
  animation: string;
  /** Optional scale multiplier */
  scale?: number;
}

export interface MovePokemonEvent extends BaseEvent {
  type: "move_pokemon";
  /** Actor ID of the Pokemon to move */
  actorId: string;
  /** Target position */
  position: Position;
  /** Duration of movement in milliseconds */
  duration: number;
  /** Easing function for movement */
  easing?: EasingType;
  /** Animation to play during movement (defaults to "Walk") */
  animation?: string;
}

export interface AnimatePokemonEvent extends BaseEvent {
  type: "animate_pokemon";
  /** Actor ID of the Pokemon to animate */
  actorId: string;
  /** Animation name to play */
  animation: string;
  /** Optional direction change */
  direction?: Direction;
  /** Duration to hold animation (0 = until next animation event) */
  duration?: number;
}

export interface RemovePokemonEvent extends BaseEvent {
  type: "remove_pokemon";
  /** Actor ID of the Pokemon to remove */
  actorId: string;
  /** Fade out duration in ms (0 = instant) */
  fadeOutDuration?: number;
}

// ============================================================================
// VFX Events
// ============================================================================

export interface PlayVFXEvent extends BaseEvent {
  type: "play_vfx";
  /** Unique ID for this VFX instance */
  vfxId: string;
  /** Path to the sprite sheet */
  spritePath: string;
  /** Position to play VFX at */
  position: Position;
  /** Optional: anchor VFX to a Pokemon actor */
  anchorToActor?: string;
  /** Scale multiplier */
  scale?: number;
  /** Alpha/opacity (0-1) */
  alpha?: number;
  /** Number of animation loops (0 = infinite) */
  loops?: number;
  /** Animation speed multiplier */
  speed?: number;
  /** Y offset from position */
  yOffset?: number;
}

export interface StopVFXEvent extends BaseEvent {
  type: "stop_vfx";
  /** VFX ID to stop */
  vfxId: string;
  /** Fade out duration in ms (0 = instant) */
  fadeOutDuration?: number;
}

// ============================================================================
// Camera Events
// ============================================================================

export interface CameraPanEvent extends BaseEvent {
  type: "camera_pan";
  /** Target position to pan to */
  target: Position;
  /** Duration of pan in milliseconds */
  duration: number;
  /** Easing function */
  easing?: EasingType;
}

export interface CameraZoomEvent extends BaseEvent {
  type: "camera_zoom";
  /** Target zoom level */
  zoom: number;
  /** Duration of zoom in milliseconds */
  duration: number;
  /** Easing function */
  easing?: EasingType;
}

export interface CameraShakeEvent extends BaseEvent {
  type: "camera_shake";
  /** Shake intensity (1 = normal) */
  intensity: number;
  /** Duration of shake in milliseconds */
  duration: number;
}

// ============================================================================
// Dialog Events
// ============================================================================

export interface DialogEvent extends BaseEvent {
  type: "dialog";
  /** Speaker name displayed above text */
  speaker: string;
  /** Optional portrait image path */
  portrait?: string;
  /** Dialog text content */
  text: string;
  /** Duration to display (ms). If 0, auto-calculates from text length */
  duration: number;
  /** Typing speed (characters per second). 0 = instant */
  typeSpeed?: number;
}

export interface HideDialogEvent extends BaseEvent {
  type: "hide_dialog";
  /** Fade out duration in ms */
  duration?: number;
}

// ============================================================================
// Utility Events
// ============================================================================

export interface WaitEvent extends BaseEvent {
  type: "wait";
  /** Duration to wait in milliseconds */
  duration: number;
}

export interface FadeEvent extends BaseEvent {
  type: "fade";
  /** Fade direction */
  direction: "in" | "out";
  /** Fade color (hex) */
  color?: string;
  /** Duration in milliseconds */
  duration: number;
}

// ============================================================================
// Union Type
// ============================================================================

export type CutsceneEvent =
  | SpawnPokemonEvent
  | MovePokemonEvent
  | AnimatePokemonEvent
  | RemovePokemonEvent
  | PlayVFXEvent
  | StopVFXEvent
  | CameraPanEvent
  | CameraZoomEvent
  | CameraShakeEvent
  | DialogEvent
  | HideDialogEvent
  | WaitEvent
  | FadeEvent;

// ============================================================================
// Cutscene Data
// ============================================================================

export interface CutsceneData {
  /** Unique cutscene identifier */
  id: string;
  /** Display name */
  name: string;
  /** Map background ID */
  mapId: string;
  /** Camera configuration */
  camera: CameraConfig;
  /** Ordered list of events */
  events: CutsceneEvent[];
}

// ============================================================================
// Editor State Types
// ============================================================================

export interface CutsceneActor {
  id: string;
  pokemonName: string;
  position: Position;
  direction: Direction;
  animation: string;
  scale: number;
  visible: boolean;
}

export interface CutsceneEditorState {
  cutscene: CutsceneData;
  selectedEventId: string | null;
  isPlaying: boolean;
  currentEventIndex: number;
  actors: Record<string, CutsceneActor>;
  camera: {
    position: Position;
    zoom: number;
  };
}

// ============================================================================
// Event Factory Helpers
// ============================================================================

let eventIdCounter = 0;

export function generateEventId(): string {
  return `event_${Date.now()}_${eventIdCounter++}`;
}

export function createDefaultEvent(type: CutsceneEvent["type"]): CutsceneEvent {
  const base = {
    id: generateEventId(),
    blocking: true,
  };

  switch (type) {
    case "spawn_pokemon":
      return {
        ...base,
        type: "spawn_pokemon",
        actorId: `actor_${Date.now()}`,
        pokemonName: "pikachu",
        position: { x: 200, y: 300 },
        direction: "SouthEast" as Direction,
        animation: "Idle",
      };
    case "move_pokemon":
      return {
        ...base,
        type: "move_pokemon",
        actorId: "",
        position: { x: 300, y: 300 },
        duration: 1000,
        easing: "easeInOut",
      };
    case "animate_pokemon":
      return {
        ...base,
        type: "animate_pokemon",
        actorId: "",
        animation: "Idle",
      };
    case "remove_pokemon":
      return {
        ...base,
        type: "remove_pokemon",
        actorId: "",
        fadeOutDuration: 300,
      };
    case "play_vfx":
      return {
        ...base,
        type: "play_vfx",
        vfxId: `vfx_${Date.now()}`,
        spritePath: "/assets/spells/impact/melee-1-white.png",
        position: { x: 200, y: 200 },
        scale: 1,
        loops: 1,
        speed: 1,
      };
    case "stop_vfx":
      return {
        ...base,
        type: "stop_vfx",
        vfxId: "",
      };
    case "camera_pan":
      return {
        ...base,
        type: "camera_pan",
        target: { x: 200, y: 300 },
        duration: 1000,
        easing: "easeInOut",
      };
    case "camera_zoom":
      return {
        ...base,
        type: "camera_zoom",
        zoom: 1,
        duration: 500,
        easing: "easeInOut",
      };
    case "camera_shake":
      return {
        ...base,
        type: "camera_shake",
        intensity: 1,
        duration: 300,
      };
    case "dialog":
      return {
        ...base,
        type: "dialog",
        speaker: "???",
        text: "Hello, world!",
        duration: 3000,
        typeSpeed: 50,
      };
    case "hide_dialog":
      return {
        ...base,
        type: "hide_dialog",
        duration: 200,
      };
    case "wait":
      return {
        ...base,
        type: "wait",
        duration: 1000,
      };
    case "fade":
      return {
        ...base,
        type: "fade",
        direction: "out",
        color: "#000000",
        duration: 500,
      };
    default:
      throw new Error(`Unknown event type: ${type}`);
  }
}

export function createEmptyCutscene(): CutsceneData {
  return {
    id: `cutscene_${Date.now()}`,
    name: "New Cutscene",
    mapId: "map (1)",
    camera: {
      bounded: false,
      initialZoom: 1,
      initialPosition: { x: 200, y: 350 },
    },
    events: [],
  };
}





























